import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { TemporaryDataType, TemporarySetDataType } from './useTemporaryDataState'
import { ropColumns } from './static-data-columns'
import { EditableCell, EditableRow } from './editable-table-utils'
import { CustomDateTimePicker } from './components/CustomDateTimePicker'
import { emptyRop } from '../utils/emptyEntities'
import { CustomTableHeader } from './components/CustomTableHeader'
import { fetchKpiGoalsWithFilter, saveAllTemporaryData } from '../importer-api'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from '../utils/actions'
import { generateRestrictedGoalValue, getFormattedGoalLabel } from '../utils/utils'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { getSelectedRowKeys, useSelectAllRows } from '../hooks/useSelectAllRows'
import { useNavigateToNewRow } from '../hooks/useNavigateToNewRow'
import { RowSelectionType } from 'antd/lib/table/interface'
import { useMoveEntityRowToAnotherEntityRowParent } from '../hooks/useMoveEntityRowToAnotherEntityRowParent'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const TemporaryRestrictedRopsTable = memo(
    ({
        parent,
        data,
        setData,
        optionsOfTubularTypeEntity,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved
    }: {
        parent: TubularTypeDto
        data: TemporaryDataType
        setData: TemporarySetDataType
        optionsOfTubularTypeEntity: TubularTypeSelectOptions
        localErrors: LocalErrorsTypes[]
        removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
    }): ReactElement => {
        const [startDate, setStartDate] = useState<Record<number, Date | string>>(null)
        const [endDate, setEndDate] = useState<Record<number, Date | string>>(null)
        const [ropGoalOptionsByRow, setRopGoalOptionsByRow] = useState<
            Record<
                number,
                {
                    label: string
                    value: ValueUom | string
                }[]
            >
        >({})

        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            setIsValidToTransfer,
            updateValueFromCheckedEntity
        } = useContext(StaticDataImporterContext)

        const { selectAllRows } = useSelectAllRows()

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()
        const { moveRowToAnotherParent } = useMoveEntityRowToAnotherEntityRowParent()

        const rops = useMemo(
            () =>
                data.rops
                    .filter(rop => rop.parent == parent.nodeId)
                    .map(r => ({ ...r, key: String(r.rowId), parent: parent.nodeId })),
            [data.rops, parent.nodeId]
        )

        useEffect(() => {
            const newStartDate: Record<number, Date | string> = {}
            const newEndDate: Record<number, Date | string> = {}
            for (const rop of data.rops) {
                newStartDate[rop.rowId] = rop.start ? new Date(rop.start) : ''
                newEndDate[rop.rowId] = rop.end ? new Date(rop.end) : ''
                if (rop?.restrictedRopSpeedValue) {
                    rop.restrictedRopSpeed = {
                        value: rop.restrictedRopSpeedValue,
                        name: rop.restrictedRopSpeedUom,
                        id: 0,
                        attribute_id: null,
                        unitOfMeasure_id: null
                    }
                }
            }
            setStartDate(newStartDate)
            setEndDate(newEndDate)
        }, []) // eslint-disable-line react-hooks/exhaustive-deps

        useEffect(() => {
            // Load the Goal options for each existing ROP restriction according to its own Restricted ROP type.
            // Restricted-ROP-type options load asynchronously in the parent, so this must re-run once they
            // arrive instead of only firing once on mount.
            rops.forEach(rop => {
                if (rop?.restrictedRopType) getKpiGoalOptionsWithFilter(rop)
            })
        }, [rops, getKpiGoalOptionsWithFilter])

        // Functions
        const getCurrentRigId = useCallback(
            (restrictedParent: number): string => {
                const runParent = data.runs.find(run => run.nodeId === restrictedParent).parent
                const sectionParent = data.sections.find(section => section.nodeId === runParent).parent
                const interventionParent = data.interventions.find(
                    intervention => intervention.nodeId === sectionParent
                ).parent
                const wellRigId = data.wells.find(well => well.nodeId === interventionParent).rigId

                return String(wellRigId)
            },
            [data.interventions, data.runs, data.sections, data.wells]
        )

        const getKpiGoalOptionsWithFilter = useCallback(
            (record: TubularRestrictedRopDto): void => {
                const tubularType = (optionsOfTubularTypeEntity?.tubularType as RestrictedSelectOptions[])?.find(
                    type => type?.label === parent?.tubularType
                )?.acronym
                const restrictedRopType = (optionsOfTubularTypeEntity?.restrictedRopType as RestrictedSelectOptions[])?.find(
                    type => type?.label === record?.restrictedRopType
                )?.acronym

                const filter = {
                    1: tubularType,
                    2: 'ROP',
                    7: restrictedRopType
                }

                const rigId = getCurrentRigId(parent.parent)

                fetchKpiGoalsWithFilter(filter, rigId).then(res => {
                    const options: {
                        label: string
                        value: ValueUom | string
                    }[] = []
                    res.data.map(goal => {
                        const newOption: RestrictedOption = {
                            value: goal.target?.goals?.value,
                            uom: {
                                name: String(goal.target?.goals?.uom)
                            } as Uom,
                            speedAcronym: getFormattedGoalLabel(goal) ?? ''
                        }
                        options.push(generateRestrictedGoalValue((newOption as unknown) as ValueUom))
                    })
                    setRopGoalOptionsByRow(prev => ({ ...prev, [record.rowId]: options }))
                })
            },
            [
                getCurrentRigId,
                optionsOfTubularTypeEntity?.restrictedRopType,
                optionsOfTubularTypeEntity?.tubularType,
                parent.parent,
                parent?.tubularType
            ]
        )
        const handleEditData = useCallback(
            (entity: TubularRestrictedRopDto[], row: TubularRestrictedRopDto, setter: TemporarySetDataType): void => {
                const newData = [...entity]
                const index = newData.findIndex(item => row.rowId === item.rowId)
                const item = newData[index]
                if (item) {
                    saveAllTemporaryData(row, 'TUBULAR_RESTRICTED_ROP').then(saves => {
                        row.rowId = saves[0].id
                        row.key = saves[0].id
                        newData.splice(index, 1, {
                            ...item,
                            ...row
                        })
                        setter.setRops(newData)
                        setIsValidToTransfer(false)
                        updateValueFromCheckedEntity(`rops=>${row.parent}`, row)
                    })
                }
            },
            [setIsValidToTransfer, updateValueFromCheckedEntity]
        )

        const handleEditSave = useCallback(
            (row: TubularRestrictedRopDto): void => {
                handleEditData(data.rops, row, setData)
                getKpiGoalOptionsWithFilter(row)
            },
            [data.rops, getKpiGoalOptionsWithFilter, handleEditData, setData]
        )

        const setRowStartDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.rops]
                const currentRow = newData.find(row => row.rowId === rowId)
                currentRow.start = newDate ? new Date(newDate).getTime() : null
                handleEditSave(currentRow)
                // Changing startDate state
                const newStartDate = { ...startDate }
                newStartDate[rowId] = newDate
                setStartDate(newStartDate)
            },
            [data.rops, handleEditSave, startDate]
        )

        const setRowEndDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.rops]
                const currentRow = newData.find(row => row.rowId === rowId)
                currentRow.end = newDate ? new Date(newDate).getTime() : null
                handleEditSave(currentRow)
                // Changing endDate state
                const newEndDate = { ...endDate }
                newEndDate[rowId] = newDate
                setEndDate(newEndDate)
            },
            [data.rops, endDate, handleEditSave]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.rops]

            const newEntity: TubularRestrictedRopDto = {
                ...emptyRop,
                key: undefined,
                frontParentUuid: parent.frontUuid,
                parent: parent.nodeId,
                nodeId: Date.now(),
                rowId: undefined,
                frontUuid: uuidv4()
            }

            saveAllTemporaryData(newEntity, 'TUBULAR_RESTRICTED_ROP').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setRops(oldEntity)
                goToNewRow()
            })
        }, [data.rops, goToNewRow, parent.frontUuid, parent.nodeId, setData])

        const handleDelete = useCallback(
            (record: TubularRestrictedRopDto): void => {
                const newData = data.rops.filter(item => item.rowId !== record.rowId)
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setRops(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            [checkedRows, data.rops, formatCheckedRows, removeErrorWhenCheckedRowIsRemoved, setCheckedRows, setData]
        )

        // Element components
        const handleCalendarElementStartDate = useCallback(
            (record: TubularRestrictedRopDto): ReactElement | null => {
                if (!startDate) return null

                return (
                    <CustomDateTimePicker
                        date={startDate[record.rowId] as string}
                        setDate={setRowStartDate}
                        rowId={record.rowId}
                    />
                )
            },
            [setRowStartDate, startDate]
        )

        const handleCalendarElementEndDate = useCallback(
            (record: TubularRestrictedRopDto): ReactElement | null => {
                if (!endDate) return null

                return (
                    <CustomDateTimePicker
                        date={endDate[record.rowId] as string}
                        setDate={setRowEndDate}
                        rowId={record.rowId}
                    />
                )
            },
            [endDate, setRowEndDate]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const ropCols = useMemo(
            () =>
                ropColumns(
                    handleDelete,
                    handleEditSave,
                    optionsOfTubularTypeEntity,
                    (record: EntityDto, selectedParent: number) =>
                        moveRowToAnotherParent(record, selectedParent, handleEditSave),
                    data.tubulars,
                    handleCalendarElementStartDate,
                    handleCalendarElementEndDate,
                    localErrors,
                    ropGoalOptionsByRow
                ),
            [
                data.tubulars,
                handleCalendarElementEndDate,
                handleCalendarElementStartDate,
                handleDelete,
                handleEditSave,
                localErrors,
                moveRowToAnotherParent,
                optionsOfTubularTypeEntity,
                ropGoalOptionsByRow
            ]
        )

        const title = useCallback((): ReactElement => {
            return <CustomTableHeader title="restricted rops" handleAddNewRow={handleAddNewRow} />
        }, [handleAddNewRow])

        const rowSelection = useMemo(
            () => ({
                selectedRowKeys: getSelectedRowKeys(checkedRows, 'rops', parent),
                type: 'checkbox' as RowSelectionType,
                onChange: (selectedRowsKey: React.Key[], selectedRows: TubularRestrictedRopDto[]) => {
                    selectAllRows({
                        data,
                        selectedRows,
                        parentNodeId: parent.nodeId,
                        type: 'rops'
                    })
                },
                onSelect: (
                    record: EntityDto,
                    selected: boolean,
                    selectedRows: TubularRestrictedRopDto[],
                    nativeEvent: Event
                ) => {
                    selectAllRows({
                        data,
                        parentNodeId: parent.nodeId,
                        type: 'rops',
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                }
            }),
            [checkedRows, data, parent, selectAllRows]
        )

        const expandable = useMemo(
            () => ({
                defaultExpandAllRows: false
            }),
            []
        )

        return (
            <TableContainer>
                <Table
                    title={title}
                    size="small"
                    components={components}
                    rowClassName={(d: EntityDto) => (d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row')}
                    bordered
                    dataSource={rops}
                    columns={ropCols as ColumnTypes}
                    pagination={false}
                    rowSelection={rowSelection}
                    expandable={expandable}
                />
            </TableContainer>
        )
    }
)

TemporaryRestrictedRopsTable.displayName = 'TemporaryRestrictedRopsTable'

export default TemporaryRestrictedRopsTable

const TableContainer = styled.main``
